import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { cn } from '../ui/cn';

/**
 * [MOD-04 AB#188] Seletor de data + hora estilo Google Calendar.
 *
 * Drop-in dos `<input type="datetime-local">` da agenda — mesmo contrato
 * `value`/`onChange` no formato `YYYY-MM-DDTHH:mm` (igual ao `paraIsoLocal`).
 *
 * Layout: dois botões na mesma linha —
 *  - Data ("12 de jun. de 2026") → popover com calendário mensal.
 *  - Hora ("15:00") → popover com lista de horários (00:00–23:45, 15 min),
 *    rolada até o horário atual, destacado. Sem wrap circular, sem spinner
 *    nativo do browser.
 *
 * Popovers vão num portal (document.body) com `position: fixed` ancorado
 * ao botão — o corpo do Modal tem `overflow-hidden`/`overflow-y-auto` e
 * recortaria um popover posicionado por dentro.
 */

interface SeletorDataHoraProps {
  /** `YYYY-MM-DDTHH:mm` (local) ou `''`. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/;
const pad = (n: number): string => String(n).padStart(2, '0');

const MESES_CURTO = [
  'jan.', 'fev.', 'mar.', 'abr.', 'mai.', 'jun.',
  'jul.', 'ago.', 'set.', 'out.', 'nov.', 'dez.',
];
const MESES_LONGO = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];
const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

/** "00:00" … "23:45" em passos de 15 min. */
const HORARIOS: string[] = Array.from({ length: 96 }, (_, i) =>
  `${pad(Math.floor(i / 4))}:${pad((i % 4) * 15)}`,
);

const BOTAO =
  'h-10 rounded-md border border-border-v2 bg-bg-canvas-v2 px-3 text-sm text-text-primary text-left ' +
  'hover:border-text-faint-v2 disabled:cursor-not-allowed disabled:opacity-50';

export function SeletorDataHora({
  value,
  onChange,
  disabled,
}: SeletorDataHoraProps): JSX.Element {
  const m = RE.exec(value ?? '');
  const ano = m ? Number(m[1]) : 0;
  const mes = m ? Number(m[2]) : 0; // 1-12
  const dia = m ? Number(m[3]) : 0;
  const hora = m ? Number(m[4]) : 0;
  const minuto = m ? Number(m[5]) : 0;
  const temData = m !== null;

  const [aberto, setAberto] = useState<null | 'data' | 'hora'>(null);
  const refData = useRef<HTMLButtonElement>(null);
  const refHora = useRef<HTMLButtonElement>(null);

  const emitirData = (a: number, mes0: number, d: number): void => {
    onChange(`${a}-${pad(mes0 + 1)}-${pad(d)}T${pad(hora)}:${pad(minuto)}`);
  };
  const emitirHora = (h: number, mm: number): void => {
    let a = ano;
    let mes1 = mes;
    let d = dia;
    if (!temData) {
      const t = new Date();
      a = t.getFullYear();
      mes1 = t.getMonth() + 1;
      d = t.getDate();
    }
    onChange(`${a}-${pad(mes1)}-${pad(d)}T${pad(h)}:${pad(mm)}`);
  };

  return (
    <div className="flex items-center gap-2">
      <button
        ref={refData}
        type="button"
        disabled={disabled}
        onClick={() => setAberto((a) => (a === 'data' ? null : 'data'))}
        className={cn(BOTAO, 'min-w-0 flex-1 truncate')}
      >
        {temData ? `${dia} de ${MESES_CURTO[mes - 1]} de ${ano}` : 'Selecionar data'}
      </button>
      <button
        ref={refHora}
        type="button"
        disabled={disabled}
        onClick={() => setAberto((a) => (a === 'hora' ? null : 'hora'))}
        className={cn(BOTAO, 'shrink-0 tabular-nums')}
      >
        {temData ? `${pad(hora)}:${pad(minuto)}` : '--:--'}
      </button>

      <Popover
        anchorRef={refData}
        open={aberto === 'data'}
        onClose={() => setAberto(null)}
      >
        <Calendario
          selAno={temData ? ano : 0}
          selMes={mes}
          selDia={dia}
          onSelecionar={(a, mes0, d) => {
            emitirData(a, mes0, d);
            setAberto(null);
          }}
        />
      </Popover>

      <Popover
        anchorRef={refHora}
        open={aberto === 'hora'}
        onClose={() => setAberto(null)}
        width={140}
      >
        <ListaHorarios
          hora={hora}
          minuto={minuto}
          onSelecionar={(h, mm) => {
            emitirHora(h, mm);
            setAberto(null);
          }}
        />
      </Popover>
    </div>
  );
}

/**
 * Popover ancorado a um botão, via portal (escapa do `overflow-hidden` do
 * Modal). `position: fixed` recalculado no scroll (capture, pega o scroll
 * interno do Modal) e no resize. Fecha ao clicar fora (botão + popover).
 */
export function Popover({
  anchorRef,
  open,
  onClose,
  children,
  width,
}: {
  anchorRef: RefObject<HTMLElement>;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  width?: number;
}): JSX.Element | null {
  const popRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const atualizar = (): void => {
      const r = anchorRef.current?.getBoundingClientRect();
      if (r) setPos({ top: r.bottom + 4, left: r.left });
    };
    atualizar();
    window.addEventListener('scroll', atualizar, true);
    window.addEventListener('resize', atualizar);
    return () => {
      window.removeEventListener('scroll', atualizar, true);
      window.removeEventListener('resize', atualizar);
    };
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent): void => {
      const t = e.target as Node;
      if (popRef.current?.contains(t)) return;
      if (anchorRef.current?.contains(t)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, onClose, anchorRef]);

  if (!open || !pos) return null;
  return createPortal(
    <div
      ref={popRef}
      // [fix AB#189] Portal vai pra document.body, mas eventos React
      // borbulham pela ÁRVORE React até o overlay do Modal pai. Sem isto,
      // selecionar dia/horário fechava o modal. Barra a propagação do
      // clique/mousedown aqui.
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      style={{ position: 'fixed', top: pos.top, left: pos.left, width }}
      className="z-[110] overflow-hidden rounded-lg border border-border-v2 bg-bg-elevated shadow-xl"
    >
      {children}
    </div>,
    document.body,
  );
}

/** Células do mês (com `null` nas posições antes do dia 1). */
function diasDoMes(ano: number, mes0: number): Array<number | null> {
  const primeiroDiaSemana = new Date(ano, mes0, 1).getDay(); // 0=Dom
  const totalDias = new Date(ano, mes0 + 1, 0).getDate();
  const cells: Array<number | null> = [];
  for (let i = 0; i < primeiroDiaSemana; i++) cells.push(null);
  for (let d = 1; d <= totalDias; d++) cells.push(d);
  return cells;
}

export function Calendario({
  selAno,
  selMes,
  selDia,
  onSelecionar,
  min,
}: {
  selAno: number;
  selMes: number; // 1-12 (0 = nenhum)
  selDia: number;
  /** `YYYY-MM-DD`: dias ANTERIORES ficam desabilitados (cinza, cursor not-allowed, sem hover, aria-disabled). */
  min?: string;
  onSelecionar: (ano: number, mes0: number, dia: number) => void;
}): JSX.Element {
  const [vis, setVis] = useState<{ ano: number; mes0: number }>(() => {
    if (selAno) return { ano: selAno, mes0: selMes - 1 };
    const t = new Date();
    return { ano: t.getFullYear(), mes0: t.getMonth() };
  });
  const cells = useMemo(() => diasDoMes(vis.ano, vis.mes0), [vis]);

  const hoje = new Date();
  const ehHoje = (d: number): boolean =>
    hoje.getFullYear() === vis.ano &&
    hoje.getMonth() === vis.mes0 &&
    hoje.getDate() === d;
  const ehSelecionado = (d: number): boolean =>
    selAno === vis.ano && selMes - 1 === vis.mes0 && selDia === d;
  const antesDoMin = (d: number): boolean =>
    !!min && `${vis.ano}-${String(vis.mes0 + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}` < min;

  const navegar = (delta: -1 | 1): void => {
    setVis((v) => {
      const m0 = v.mes0 + delta;
      if (m0 < 0) return { ano: v.ano - 1, mes0: 11 };
      if (m0 > 11) return { ano: v.ano + 1, mes0: 0 };
      return { ano: v.ano, mes0: m0 };
    });
  };

  return (
    <div className="w-64 p-3">
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          aria-label="Mês anterior"
          onClick={() => navegar(-1)}
          className="rounded p-1 text-text-tertiary hover:bg-bg-muted hover:text-text-primary"
        >
          <IconChevronLeft size={16} />
        </button>
        <span className="text-sm font-semibold text-text-primary">
          {MESES_LONGO[vis.mes0]} {vis.ano}
        </span>
        <button
          type="button"
          aria-label="Próximo mês"
          onClick={() => navegar(1)}
          className="rounded p-1 text-text-tertiary hover:bg-bg-muted hover:text-text-primary"
        >
          <IconChevronRight size={16} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {DIAS_SEMANA.map((d) => (
          <div
            key={d}
            className="py-1 text-center text-[11px] font-medium text-text-tertiary"
          >
            {d}
          </div>
        ))}
        {cells.map((d, i) =>
          d === null ? (
            <div key={`v-${i}`} />
          ) : (
            <button
              key={d}
              type="button"
              aria-disabled={antesDoMin(d) || undefined}
              data-desabilitado={antesDoMin(d) ? 'true' : undefined}
              onClick={() => {
                if (antesDoMin(d)) return;
                onSelecionar(vis.ano, vis.mes0, d);
              }}
              className={cn(
                'aspect-square rounded-md text-sm',
                antesDoMin(d)
                  ? 'cursor-not-allowed text-text-faint-v2 opacity-50'
                  : ehSelecionado(d)
                    ? 'bg-primary font-semibold text-on-primary'
                    : 'text-text-primary hover:bg-bg-muted',
                !ehSelecionado(d) && ehHoje(d) && 'ring-1 ring-primary',
              )}
            >
              {d}
            </button>
          ),
        )}
      </div>
    </div>
  );
}

function ListaHorarios({
  hora,
  minuto,
  onSelecionar,
}: {
  hora: number;
  minuto: number;
  onSelecionar: (hora: number, minuto: number) => void;
}): JSX.Element {
  const atual = `${pad(hora)}:${pad(minuto)}`;
  // Item para rolar até: o atual se for múltiplo de 15; senão o piso de 15.
  const alvo = HORARIOS.includes(atual)
    ? atual
    : `${pad(hora)}:${pad(Math.floor(minuto / 15) * 15)}`;

  const contRef = useRef<HTMLDivElement>(null);
  const alvoRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const c = contRef.current;
    const s = alvoRef.current;
    if (c && s) {
      // Rola só o container (não a página/modal).
      c.scrollTop = s.offsetTop - c.clientHeight / 2 + s.clientHeight / 2;
    }
  }, []);

  return (
    <div ref={contRef} className="relative max-h-60 overflow-y-auto py-1">
      {HORARIOS.map((t) => {
        const [h, mm] = t.split(':').map(Number) as [number, number];
        const ehSel = t === atual;
        return (
          <button
            key={t}
            ref={t === alvo ? alvoRef : undefined}
            type="button"
            onClick={() => onSelecionar(h, mm)}
            className={cn(
              'block w-full px-4 py-1.5 text-left text-sm tabular-nums',
              ehSel
                ? 'bg-primary font-semibold text-on-primary'
                : 'text-text-primary hover:bg-bg-muted',
            )}
          >
            {t}
          </button>
        );
      })}
    </div>
  );
}
